import { State } from '@progress/kendo-data-query';
import { isBefore, isWithinInterval } from 'date-fns';
import { TFunction } from 'i18next';

import { isEqual, toNumber } from 'lodash';
import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  CanAdjustBudgetBalance,
  CanManageBudgets,
  CanManageGLCodeAttributes,
  CanManageGLCodes,
  CanOverrideBudgets,
  CanUsesInitialBudgets,
} from 'src/components/guards/PermissionsList';
import useBoolean from 'src/hooks/useBoolean';
import useCompany from 'src/hooks/useCompany';
import useConfirm from 'src/hooks/useConfirm';
import usePermission from 'src/hooks/usePermissions';
import usePostMessage from 'src/hooks/usePostMessage';
import { ICommittedAmountTransaction } from 'src/services/useCommittedAmountApi';
import useGLCodeBudgetSaveApi, {
  GLCodeBudgetSaveRequest,
} from 'src/services/useGLCodeBudgetSaveApi';
import useGLPeriodApi, {
  GLPeriodResponse,
  GLPeriodResponseObject,
  GLPeriods,
} from 'src/services/useGLPeriodApi';
import { ActualizedAmountTransaction } from 'src/services/useGetActualizedTransactionApi';
import useGetClientApi, { ClientResponse } from 'src/services/useGetClientApi';
import useGetGlMasks, { GlMasksResponse } from 'src/services/useGetGlMasks';
import useGLCodeDepartmentApi, {
  GLCodePeriodDepartment,
  GLCodePeriodDepartmentResponse,
} from 'src/services/useGlCodeDepartmentApi';
import useGLCodePeriodBudgetApi, {
  GLCodePeriodBudgetResponse,
} from 'src/services/useGlCodePeriodBudgetApi';
import useGlCodesBudgetByYearApi, {
  GLCodesBudgetByYearResponseObject,
} from 'src/services/useGlCodesBudgetByYearApi';
import { IHoldAmountTransaction } from 'src/services/useHoldAmountApi';
import { Company } from 'src/types/Companies';
import { SetState } from 'src/types/generic';
import { extractNumericPart, generateRegexPattern } from 'src/utils/constants';
import { getCurrentMonth, getCurrentYear, removeTime } from 'src/utils/formatTime';
import {
  GLPeriodDropdown,
  GLPeriodDropdownChildren,
  GLPeriodYearDropdown,
  formatGLPeriodDropDown,
  formatGLYearDropDown,
} from 'src/utils/transformation';
import useCheckbookPeriodErrors from './useCheckbookPeriodErrors';
import useCheckbookYearErrors from './useCheckbookYearErrors';

type CheckbookModalType =
  | 'holdAmount'
  | 'actualizedAmount'
  | 'committedAmount'
  | 'logAmount'
  | 'departments';

interface ApprovalAssignedUsers {
  users: ClientResponse[];
  title: string;
  glCodeDescription: string;
}

interface ViewCheckbookContextType {
  dropDownPeriodValues: GLPeriodDropdown[];
  dropDownYearValues: GLPeriodYearDropdown[];
  dropDownPeriodValue: GLPeriodDropdownChildren | null;
  dropDownYearValue: GLPeriodYearDropdown | null;
  setDropDownPeriodValue: SetState<GLPeriodDropdownChildren | null>;
  setDropDownYearValue: SetState<GLPeriodYearDropdown | null>;
  isDropDownLoading: boolean;
  glCodePeriodBudget: GLCodePeriodBudgetResponse[];
  isGlCodesBudgetLoading: boolean;
  isGlCodesBudgetByYearLoading: boolean;
  periodCheckbookDataState: State;
  yearCheckbookDataState: State;
  setPeriodCheckbookDataState: SetState<State>;
  setYearCheckbookDataState: SetState<State>;
  glPeriodIdByPeriod?: number;
  fiscalYear?: number;
  companyId?: string;
  expand: boolean;
  toggleExpand: () => void;
  holdAmountData: IHoldAmountTransaction[];
  setHoldAmountData: SetState<IHoldAmountTransaction[]>;
  actualizedAmountData: ActualizedAmountTransaction[];
  setActualizedAmountData: SetState<ActualizedAmountTransaction[]>;
  balanceType: CheckbookModalType;
  setBalanceType: SetState<CheckbookModalType>;
  setToggleExpandFalse: () => void;
  setGlCodePeriodBudget: SetState<GLCodePeriodBudgetResponse[]>;
  setOriginalGlCodePeriodBudget: SetState<GLCodePeriodBudgetResponse[]>;
  setGlCodesBudgetByYear: SetState<GLCodesBudgetByYearResponseObject[]>;
  setOriginalGlCodeYearBudget: SetState<GLCodesBudgetByYearResponseObject[]>;
  t: TFunction<'translation', undefined>;
  isEditMode: boolean;
  setIsEditMode: SetState<boolean>;
  selectedGLBudget: GLCodePeriodBudgetResponse;
  setSelectedGLBudget: SetState<GLCodePeriodBudgetResponse>;
  editingbudgetPeriodDataWithErrors: GLCodePeriodBudgetResponse[];
  glCodesBudgetByYear: GLCodesBudgetByYearResponseObject[];
  periodErrorList: string[] | ReactNode[];
  uniquePeriodErrorList: string[];
  yearErrorList: string[];
  periodWarningList: string[];
  yearWarningList: string[];
  periodInformationList: string[];
  yearInformationList: string[];
  hasPeriodWarnings: boolean;
  hasPeriodErrors: boolean;
  hasYearErrors: boolean;
  hasYearWarnings: boolean;
  glCodeDepartments: GLCodePeriodDepartmentResponse;
  hasEditGLCodePermission: boolean;
  hasAdjustBudgetPermission: boolean;
  hasUsesInitialBudgetsPermission: boolean;
  originalGlCodePeriodBudget: GLCodePeriodBudgetResponse[];
  originalGlCodeYearBudget: GLCodesBudgetByYearResponseObject[];
  departmentList: GLCodePeriodDepartment[];
  selectedPeriodDepartment: GLCodePeriodDepartment;
  selectedYearDepartment: GLCodePeriodDepartment;
  setSelectedPeriodDepartment: SetState<GLCodePeriodDepartment>;
  setSelectedYearDepartment: SetState<GLCodePeriodDepartment>;
  selectedCompany: Company;
  selectedView: number;
  setSelectedView: SetState<number>;
  isPeriodView: boolean;
  hasEditCheckbookPermission: boolean;
  hasRequireGlCodeFormatsPermission: boolean;
  hasManageBudgetPermission: boolean;
  hasCanOverrideBudgetsPermission: boolean;
  refreshGLCodePeriodTable: () => Promise<void>;
  getGlCodePeriodBudget: (glPeriodIdByPeriod?: number) => Promise<GLCodePeriodBudgetResponse[]>;
  month: number;
  year: number;
  confirmBeforeProceeding: (acceptCallback: VoidFunction) => void;
  isPeriodTableDirty: boolean;
  isYearTableDirty: boolean;
  pageTitle: string;
  setEditingGlCodeYearBudget: SetState<GLCodesBudgetByYearResponseObject[]>;
  setEditingGlCodePeriodBudget: SetState<GLCodePeriodBudgetResponse[]>;
  editingGlCodeYearBudget: GLCodesBudgetByYearResponseObject[];
  dropDownIndexByYear: number;
  glPeriods: GLPeriods;
  getGLPeriods: (params?: undefined) => Promise<GLPeriodResponse>;
  editingBudgetYearDataWithErrors: GLCodesBudgetByYearResponseObject[];
  buId: string;
  handleSaveClick: VoidFunction;
  committedAmountData: ICommittedAmountTransaction[];
  setCommittedAmountData: SetState<ICommittedAmountTransaction[]>;
  editingGlCodePeriodBudget: GLCodePeriodBudgetResponse[];
  glCodeWarningsColumnData: any[];
  setGlCodeWarningsColumnData: Dispatch<SetStateAction<any[]>>;
  modalStepCount: number;
  setModalStepCount: Dispatch<SetStateAction<number>>;
  isModalOpen: boolean;
  handleCloseModal: () => void;
  glFormatsResponse: GlMasksResponse;
  regexPatternBE: string[];
  periodErrors: any[];
  openUsersAssigned: boolean;
  setOpenUsersAssigned: SetState<boolean>;
  clients: ClientResponse[];
  assignedUsers: ApprovalAssignedUsers;
  setAssignedUsers: SetState<ApprovalAssignedUsers>;
  hasManageGLCodeAttributesPermission: boolean;
  openWarningModal: () => void;
  isCheckbookEditable: boolean;
  isSelectedPeriodClosed: boolean;
  isYearEditable: boolean;
  isSelectedFYPeriodsClosed: boolean | undefined;
  getIsLocked: ({ field, dataItem, dropDownYearValue }: GetIsLockedParams) => GetIsLockedResult;
  isSaveGlCodeBudgetLoading: boolean;
  allDeptGLCodes: GLCodePeriodBudgetResponse[];
}

export const ViewCheckbookContext = createContext<ViewCheckbookContextType>(
  {} as ViewCheckbookContextType
);

interface GetIsLockedParams {
  field: string;
  dataItem: Record<string, any>;
  dropDownYearValue: any;
}

interface GetIsLockedResult {
  index: number;
  isLocked: boolean;
  isCurrentPeriodPassed: boolean;
  currentPeriodEditable: boolean;
}

interface GlPeriodObject {
  endDate: string;
  isClosed: boolean;
}

interface ViewCheckbookContextProps {
  children: ReactNode;
  selectedView: number;
  setSelectedView: SetState<number>;
  isPeriodView: boolean;
}

export const initialPeriodCheckbookDataState: State = {
  skip: 0,
  take: 5000,
  sort: [{ field: 'code', dir: 'asc' }],
  filter: {
    logic: 'and',
    filters: [],
  },
};

export const initialYearCheckbookDataState: State = {
  skip: 0,
  take: 5000,
  sort: [{ field: 'code', dir: 'asc' }],
  filter: {
    logic: 'and',
    filters: [],
  },
};

const defaultDepartment = { departmentId: 0, glCdId: 0, departmentName: 'All Departments' };
const year: number = getCurrentYear();
const month: number = getCurrentMonth();

const ViewCheckbookContextProvider: FC<ViewCheckbookContextProps> = ({
  children,
  selectedView,
  setSelectedView,
  isPeriodView,
}) => {
  const { company: selectedCompany } = useCompany();
  const { companyId } = selectedCompany;

  const [selectedPeriodDepartment, setSelectedPeriodDepartment] =
    useState<GLCodePeriodDepartment>(defaultDepartment);
  const [selectedYearDepartment, setSelectedYearDepartment] =
    useState<GLCodePeriodDepartment>(defaultDepartment);

  const [periodCheckbookDataState, setPeriodCheckbookDataState] = useState<State>({
    ...initialPeriodCheckbookDataState,
  });
  const [yearCheckbookDataState, setYearCheckbookDataState] = useState<State>({
    ...initialYearCheckbookDataState,
  });

  const [selectedGLBudget, setSelectedGLBudget] = useState<GLCodePeriodBudgetResponse>(
    {} as GLCodePeriodBudgetResponse
  );

  const [originalGlCodeYearBudget, setOriginalGlCodeYearBudget] = useState<
    GLCodesBudgetByYearResponseObject[]
  >([]);

  const [originalGlCodePeriodBudget, setOriginalGlCodePeriodBudget] = useState<
    GLCodePeriodBudgetResponse[]
  >([]);

  const [editingGlCodeYearBudget, setEditingGlCodeYearBudget] = useState<
    GLCodesBudgetByYearResponseObject[]
  >([]);

  const [editingGlCodePeriodBudget, setEditingGlCodePeriodBudget] = useState<
    GLCodePeriodBudgetResponse[]
  >([]);

  const [dropDownPeriodValue, setDropDownPeriodValue] = useState<GLPeriodDropdownChildren | null>(
    null
  );
  const [dropDownYearValue, setDropDownYearValue] = useState<GLPeriodYearDropdown | null>(null);
  const [holdAmountData, setHoldAmountData] = useState<IHoldAmountTransaction[]>([]);
  const [actualizedAmountData, setActualizedAmountData] = useState<ActualizedAmountTransaction[]>(
    []
  );

  const isSelectedFYPeriodsClosed = useMemo(
    () => dropDownYearValue?.periods?.every((period) => period.isClosed === 1),
    [dropDownYearValue?.periods]
  );
  const [committedAmountData, setCommittedAmountData] = useState<ICommittedAmountTransaction[]>([]);
  const { glFormatsResponse, getGlCodeFormats } = useGetGlMasks();

  useEffect(() => {
    if (companyId) getGlCodeFormats();
  }, [companyId, getGlCodeFormats]);

  const { t } = useTranslation();
  const { glPeriods, isLoading: isDropDownLoading, getGLPeriods } = useGLPeriodApi(companyId);

  const { value: isEditMode, setBool: setIsEditMode } = useBoolean(false);
  const currentDate = removeTime(new Date());

  const { regexPatternBE } = useMemo(() => {
    const regexPatternBE = glFormatsResponse?.setGlMask ? glFormatsResponse?.glMasks : [];
    return { regexPatternBE };
  }, [glFormatsResponse?.glMasks, glFormatsResponse?.setGlMask]);

  const initialDropdownValue = useMemo(
    () =>
      glPeriods.find((value) =>
        isWithinInterval(currentDate, {
          start: removeTime(new Date(value.startDate)),
          end: removeTime(new Date(value.endDate)),
        })
      ) ?? null,
    [currentDate, glPeriods]
  );

  const {
    glCodePeriodBudget,
    setGlCodePeriodBudget,
    getGlCodePeriodBudget,
    isLoading: isGlCodesBudgetLoading,
    allDeptGLCodes,
  } = useGLCodePeriodBudgetApi(companyId, selectedPeriodDepartment);

  const {
    glCodesBudgetByYear,
    setGlCodesBudgetByYear,
    getGlCodesBudgetByYear,
    isLoading: isGlCodesBudgetByYearLoading,
  } = useGlCodesBudgetByYearApi(companyId, selectedYearDepartment);

  const { confirm } = useConfirm();
  const { glCodeDepartments, getGlCodeDepartments } = useGLCodeDepartmentApi(companyId);
  const dropDownPeriodValues = useMemo(() => formatGLPeriodDropDown(glPeriods), [glPeriods]);
  const dropDownYearValues = useMemo(() => formatGLYearDropDown(glPeriods), [glPeriods]);
  const { value: expand, toggle: toggleExpand, setFalse: setToggleExpandFalse } = useBoolean(false);
  const [balanceType, setBalanceType] = useState<CheckbookModalType>('holdAmount');

  const isPeriodTableDirty = useMemo(() => {
    const originalPeriodData = originalGlCodePeriodBudget.map((obj) => ({
      ...obj,
      budgetAmount: obj.budgetAmount === null ? 0 : obj.budgetAmount,
      selected: undefined,
      remainingBudgetAmountPercentage: undefined,
      remainingBudgetAmount: undefined,
    }));
    const modifiedPeriodData = editingGlCodePeriodBudget.map((obj) => ({
      ...obj,
      selected: undefined,
      budgetAmount: Number(obj.budgetAmount),
      remainingBudgetAmountPercentage: undefined,
      remainingBudgetAmount: undefined,
    }));

    return isEditMode ? !isEqual(originalPeriodData, modifiedPeriodData) : false;
  }, [editingGlCodePeriodBudget, isEditMode, originalGlCodePeriodBudget]);

  const isYearTableDirty = useMemo(() => {
    const originalYearData = originalGlCodeYearBudget.map((obj) => ({
      ...obj,
      selected: undefined,
    }));
    const modifiedYearData = editingGlCodeYearBudget.map((obj) => ({
      ...obj,
      selected: undefined,
    }));
    return isEditMode ? !isEqual(originalYearData, modifiedYearData) : false;
  }, [editingGlCodeYearBudget, isEditMode, originalGlCodeYearBudget]);

  useEffect(() => {
    setDropDownPeriodValue((prev) => {
      let newValue: GLPeriodDropdownChildren | null = null;
      let prevValue = dropDownPeriodValues
        ?.find((value) => value.fiscalYear === prev?.fiscalYear)
        ?.items.find((value) => value.glPeriodId === prev?.glPeriodId) as GLPeriodDropdownChildren;
      if (prev && prevValue) {
        newValue = prevValue;
      } else if (initialDropdownValue) {
        newValue = dropDownPeriodValues
          ?.find((value) => value.fiscalYear === initialDropdownValue?.fiscalYear)
          ?.items.find(
            (value) => value.glPeriodId === initialDropdownValue.glPeriodId
          ) as GLPeriodDropdownChildren;
      }
      return newValue;
    });
  }, [dropDownPeriodValues, initialDropdownValue]);

  useEffect(() => {
    setDropDownYearValue(
      initialDropdownValue
        ? (dropDownYearValues.find(
            (item) => item.fiscalYear === initialDropdownValue?.fiscalYear
          ) as GLPeriodYearDropdown)
        : null
    );
  }, [dropDownYearValues, initialDropdownValue]);

  const glPeriodIdByPeriod = useMemo(() => {
    const { glPeriodId = undefined } = dropDownPeriodValue ?? ({} as GLPeriodResponseObject);
    return glPeriodId;
  }, [dropDownPeriodValue]);

  const hasRequireGlCodeFormatsPermission = glFormatsResponse?.setGlMask;

  const {
    errors: periodErrors,
    warnings: periodWarnings,
    infos: periodInfos,
    periodErrorList,
    uniquePeriodErrorList,
    periodWarningList,
    periodInformationList,
    hasPeriodErrors,
    hasPeriodWarnings,
    hasUnsavedPeriodChanges,
  } = useCheckbookPeriodErrors(
    editingGlCodePeriodBudget,
    originalGlCodePeriodBudget,
    glCodePeriodBudget,
    isEditMode,
    regexPatternBE,
    glFormatsResponse
  );

  const {
    errors: yearErrors,
    warnings: yearWarnings,
    infos: yearInfos,
    yearErrorList,
    yearWarningList,
    yearInformationList,
    hasYearErrors,
    hasYearWarnings,
    hasUnsavedYearChanges,
  } = useCheckbookYearErrors(
    editingGlCodeYearBudget,
    originalGlCodeYearBudget,
    glCodesBudgetByYear,
    isEditMode,
    regexPatternBE,
    glFormatsResponse
  );

  const { saveGlCodeBudget, isLoading: isSaveGlCodeBudgetLoading } = useGLCodeBudgetSaveApi(
    selectedCompany.companyId
  );
  const { clients, getClients } = useGetClientApi(companyId);

  const fiscalYear = useMemo(() => {
    const { fiscalYear = undefined } = dropDownYearValue ?? ({} as GLPeriodYearDropdown);
    return fiscalYear;
  }, [dropDownYearValue]);

  const dropDownIndexByYear = useMemo(
    () => dropDownYearValues.findIndex((period) => period.fiscalYear === fiscalYear),
    [dropDownYearValues, fiscalYear]
  );

  const fiscalYearByYear = dropDownYearValues[dropDownIndexByYear]?.fiscalYear;

  const refreshGLCodePeriodTable = useCallback(async () => {
    if (selectedView === 0) {
      const res = await getGlCodePeriodBudget(glPeriodIdByPeriod);
      setEditingGlCodePeriodBudget(res);
    } else {
      const res = await getGlCodesBudgetByYear(fiscalYearByYear);
      setEditingGlCodeYearBudget(res);
    }
  }, [
    selectedView,
    getGlCodePeriodBudget,
    glPeriodIdByPeriod,
    getGlCodesBudgetByYear,
    fiscalYearByYear,
  ]);

  useEffect(() => {
    if (fiscalYearByYear && selectedView === 1) {
      getGlCodesBudgetByYear(fiscalYearByYear);
    } else {
      setGlCodesBudgetByYear([]);
    }
  }, [fiscalYearByYear, getGlCodesBudgetByYear, selectedView, setGlCodesBudgetByYear]);

  const departmentList = useMemo(
    () =>
      glCodeDepartments?.departments
        ? [defaultDepartment, ...glCodeDepartments?.departments]
        : [defaultDepartment],
    [glCodeDepartments?.departments]
  );

  const getIsLocked = ({
    field,
    dataItem,
    dropDownYearValue,
  }: GetIsLockedParams): GetIsLockedResult => {
    const index = toNumber(extractNumericPart(field));
    const isClosedKey = `_isClosed${index}`;
    const isClosedValue = dataItem[isClosedKey];
    const isLocked = Number(isClosedValue) === 1;
    const currentPeriod = dropDownYearValue?.periods[index - 1] as GlPeriodObject;
    const currentDate = removeTime(new Date());
    const currentPeriodEndDate = removeTime(new Date(currentPeriod?.endDate));
    const isCurrentPeriodPassed = isBefore(currentPeriodEndDate, currentDate);
    const currentPeriodEditable = !currentPeriod?.isClosed;
    return {
      index,
      isLocked,
      isCurrentPeriodPassed,
      currentPeriodEditable,
    };
  };

  const hasManageBudgetPermission: boolean = usePermission({ permissions: CanManageBudgets });
  const hasEditGLCodePermission: boolean = usePermission({ permissions: CanManageGLCodes });
  const hasCanOverrideBudgetsPermission: boolean = usePermission({
    permissions: CanOverrideBudgets,
  });

  const checkEditableStatus = useCallback(
    (dataItem: GLCodesBudgetByYearResponseObject) => {
      const noofperiods = dropDownYearValue?.periods?.length ?? 12;
      const budgetKeys = Array.from({ length: noofperiods }).map(
        (_, indx) => `initialBudgetPeriod${(indx + 1).toString().padStart(2, '0')}`
      );
      const budgetEditableValues = budgetKeys.map((key) => {
        const details = getIsLocked({
          field: key,
          dataItem,
          dropDownYearValue,
        });
        const { index, isLocked, isCurrentPeriodPassed, currentPeriodEditable } = details;

        const editable =
          !isLocked &&
          isEditMode &&
          !dataItem.isDeleted &&
          !isCurrentPeriodPassed &&
          hasManageBudgetPermission &&
          currentPeriodEditable &&
          dropDownYearValue
            ? hasCanOverrideBudgetsPermission
              ? true
              : dataItem[
                  `_initialBudgetPeriod${index
                    .toString()
                    .padStart(2, '0')}` as keyof GLCodesBudgetByYearResponseObject
                ] === null
            : false;
        return editable;
      });
      return budgetEditableValues.some((editStatus) => editStatus);
    },
    [dropDownYearValue, hasCanOverrideBudgetsPermission, hasManageBudgetPermission, isEditMode]
  );

  const editingbudgetPeriodDataWithErrors: GLCodePeriodBudgetResponse[] = useMemo(
    () =>
      editingGlCodePeriodBudget.map((item, index) => ({
        ...item,
        errors: periodErrors?.[index],
        warnings: periodWarnings?.[index],
        informations: periodInfos?.[index],
        hasErrorsOrWarnings: !!(
          (periodErrors?.[index] && Object?.values(periodErrors?.[index])?.flat()?.length > 0) ||
          (periodWarnings?.[index] &&
            Object?.values(periodWarnings?.[index])?.flat()?.length > 0) ||
          (periodInfos?.[index] && Object?.values(periodInfos?.[index])?.flat()?.length > 0)
        ),
      })),
    [editingGlCodePeriodBudget, periodErrors, periodInfos, periodWarnings]
  );

  const editingBudgetYearDataWithErrors: GLCodesBudgetByYearResponseObject[] = useMemo(
    () =>
      editingGlCodeYearBudget.map((item, index) => {
        const isBudgetCopyAcrossEnabled = checkEditableStatus(item);
        return {
          ...item,
          isBudgetCopyAcrossEnabled,
          errors: yearErrors?.[index],
          warnings: yearWarnings?.[index],
          informations: yearInfos?.[index],
          hasErrorsOrWarnings: !!(
            (yearErrors?.[index] && Object?.values(yearErrors?.[index])?.flat()?.length > 0) ||
            (yearWarnings?.[index] && Object?.values(yearWarnings?.[index])?.flat()?.length > 0) ||
            (yearInfos?.[index] && Object?.values(yearInfos?.[index])?.flat()?.length > 0)
          ),
        };
      }),
    [checkEditableStatus, editingGlCodeYearBudget, yearErrors, yearInfos, yearWarnings]
  );

  const hasAdjustBudgetPermission = usePermission({ permissions: CanAdjustBudgetBalance });
  const hasUsesInitialBudgetsPermission: boolean = usePermission({
    permissions: CanUsesInitialBudgets,
    companyAttribute: true,
  });
  const hasManageGLCodeAttributesPermission: boolean = usePermission({
    permissions: CanManageGLCodeAttributes,
  });

  const hasEditCheckbookPermission = usePermission({
    permissions: [
      ...CanManageGLCodes,
      ...CanManageBudgets,
      ...CanAdjustBudgetBalance,
      ...CanManageGLCodeAttributes,
    ],
    or: true,
  });

  useEffect(() => {
    if (companyId) {
      getGLPeriods();
      getGlCodeDepartments();
      getClients();
    }
  }, [companyId, getClients, getGLPeriods, getGlCodeDepartments]);

  useEffect(() => {
    if (glPeriodIdByPeriod && selectedView === 0) {
      getGlCodePeriodBudget(glPeriodIdByPeriod);
    }
  }, [companyId, getGlCodePeriodBudget, glPeriodIdByPeriod, selectedView, setGlCodePeriodBudget]);

  const confirmBeforeProceeding = useCallback(
    (acceptCallback: VoidFunction) => {
      if (
        isEditMode &&
        (isPeriodView
          ? isPeriodTableDirty || hasUnsavedPeriodChanges
          : isYearTableDirty || hasUnsavedYearChanges)
      ) {
        confirm({
          title: 'Unsaved Changes',
          content:
            'Unsaved changes will be lost by proceeding. Cancel to return and save your changes.',
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Discard and Proceed',
          width: '725px',
        }).then((isAccepted) => {
          if (isAccepted) {
            acceptCallback();
            setIsEditMode(false);
            setEditingGlCodePeriodBudget([]);
            setOriginalGlCodePeriodBudget([]);
            setEditingGlCodeYearBudget([]);
            setOriginalGlCodeYearBudget([]);
          }
        });
      } else {
        acceptCallback();
      }
    },
    [
      confirm,
      hasUnsavedPeriodChanges,
      hasUnsavedYearChanges,
      isEditMode,
      isPeriodTableDirty,
      isPeriodView,
      isYearTableDirty,
      setIsEditMode,
    ]
  );

  const pageTitle = useMemo(
    () => (hasEditCheckbookPermission ? 'View/Edit Checkbook' : 'View Checkbook'),
    [hasEditCheckbookPermission]
  );

  const postMessageData = useMemo(
    () => ({
      isDirty:
        isPeriodTableDirty || hasUnsavedPeriodChanges || isYearTableDirty || hasUnsavedYearChanges,
    }),
    [hasUnsavedPeriodChanges, hasUnsavedYearChanges, isPeriodTableDirty, isYearTableDirty]
  );

  const { value: isModalOpen, setFalse: handleCloseModal, setTrue: handleOpenModal } = useBoolean();
  const [glCodeWarningsColumnData, setGlCodeWarningsColumnData] = useState<any[]>([]);
  const [modalStepCount, setModalStepCount] = useState(0);
  const [openUsersAssigned, setOpenUsersAssigned] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState<ApprovalAssignedUsers>(
    {} as ApprovalAssignedUsers
  );

  const openWarningModal = useCallback(() => {
    if (glCodeWarningsColumnData?.length) {
      setModalStepCount(0);
    }
    handleOpenModal();
  }, [glCodeWarningsColumnData?.length, handleOpenModal]);

  const handleSaveClick = useCallback(async () => {
    let updatedWarnings = [];
    if (hasRequireGlCodeFormatsPermission) {
      const activeItems = editingGlCodePeriodBudget?.filter((el: any) => el.isActive);

      if (activeItems) {
        updatedWarnings = activeItems.filter((item: any) => {
          const regex = generateRegexPattern(regexPatternBE, item.code?.toUpperCase());
          return !regex;
        });
        setGlCodeWarningsColumnData(updatedWarnings);
      }
    }
    const unDeletedSaveData = editingbudgetPeriodDataWithErrors.filter(
      (code) =>
        !code.isDeleted &&
        code.warnings?.warningMessages.some((message) => message.includes('unsaved changes'))
    );
    let isNewGlCodeExists = unDeletedSaveData.some((code) => code.isNewGlCode);

    let saveData: GLCodeBudgetSaveRequest[] = unDeletedSaveData.map((item, index) => {
      const { id, code, description, isFixed, isActive, budgetAmount, initialBudget } = item;
      const regex = generateRegexPattern(regexPatternBE, item.code);
      const isFormattedGLCode = regex;
      const isActiveOrginal = originalGlCodePeriodBudget[index]?.isActive;
      const { glPeriodId, startDate, endDate } = dropDownPeriodValue as GLPeriodDropdownChildren;
      const newItem: GLCodeBudgetSaveRequest = {
        id,
        code: code.trim().toUpperCase(),
        description: description.trim(),
        isActive:
          !hasRequireGlCodeFormatsPermission ||
          (hasRequireGlCodeFormatsPermission && isFormattedGLCode) ||
          (!isActive && hasRequireGlCodeFormatsPermission && !isFormattedGLCode)
            ? isActive
            : isActiveOrginal,
        isFixed,
        periodBudgets: [
          {
            glPeriodId,
            glCdId: id,
            startDate,
            endDate,
            totalBudgetAmount: Number(budgetAmount),
            initialBudget:
              initialBudget === null || (initialBudget as any) === ''
                ? null
                : Number(initialBudget),
          },
        ],
      };
      return newItem;
    });

    saveGlCodeBudget(saveData).then(() => {
      refreshGLCodePeriodTable();
      setIsEditMode(false);
      setPeriodCheckbookDataState((prev) => ({ ...prev, sort: [{ field: 'code', dir: 'asc' }] }));
      if (glCodeDepartments.clientHasDepartment && isNewGlCodeExists) {
        confirm({
          title: 'GL Code(s) Successfully Updated',
          content: (
            <div>
              GL Codes have been updated successfully. Newly created GL Codes will need to be
              assigned to your department to be viewable in View Checkbook. If you do not have
              access to add codes to departments, please contact your administrator to add them for
              you.
            </div>
          ),
          cancelButtonText: 'Ok',
          hasNoConfirmButton: true,
          width: '600px',
        });
      } else {
        toast.success('GL Code(s) has been successfully updated. Table sort set back to default.');
      }
      if (updatedWarnings?.length) {
        openWarningModal();
      }
    });
  }, [
    confirm,
    dropDownPeriodValue,
    editingGlCodePeriodBudget,
    editingbudgetPeriodDataWithErrors,
    glCodeDepartments.clientHasDepartment,
    hasRequireGlCodeFormatsPermission,
    openWarningModal,
    originalGlCodePeriodBudget,
    refreshGLCodePeriodTable,
    regexPatternBE,
    saveGlCodeBudget,
    setIsEditMode,
  ]);

  usePostMessage(postMessageData);

  const isSelectedPeriodClosed = useMemo(
    () => (dropDownPeriodValue?.isClosed === 1 ? true : false),
    [dropDownPeriodValue?.isClosed]
  );

  const isCheckbookEditable = useMemo(
    () => (hasEditCheckbookPermission && !isSelectedPeriodClosed ? true : false),
    [hasEditCheckbookPermission, isSelectedPeriodClosed]
  );

  const isYearEditable = useMemo(
    () => (isSelectedFYPeriodsClosed ? false : hasEditCheckbookPermission ? true : false),
    [hasEditCheckbookPermission, isSelectedFYPeriodsClosed]
  );

  return (
    <ViewCheckbookContext.Provider
      value={{
        dropDownPeriodValues,
        dropDownYearValues,
        dropDownPeriodValue,
        dropDownYearValue,
        setDropDownPeriodValue,
        setDropDownYearValue,
        isDropDownLoading,
        glCodePeriodBudget,
        isGlCodesBudgetLoading,
        isGlCodesBudgetByYearLoading,
        periodCheckbookDataState,
        yearCheckbookDataState,
        setPeriodCheckbookDataState,
        setYearCheckbookDataState,
        glPeriodIdByPeriod,
        fiscalYear,
        companyId,
        expand,
        toggleExpand,
        holdAmountData,
        setHoldAmountData,
        actualizedAmountData,
        setActualizedAmountData,
        balanceType,
        setBalanceType,
        setToggleExpandFalse,
        setGlCodePeriodBudget,
        isEditMode,
        setIsEditMode,
        t,
        periodErrorList,
        yearErrorList,
        periodWarningList,
        yearWarningList,
        periodInformationList,
        yearInformationList,
        hasPeriodErrors,
        hasPeriodWarnings,
        hasYearErrors,
        hasYearWarnings,
        editingbudgetPeriodDataWithErrors,
        editingBudgetYearDataWithErrors,
        glCodesBudgetByYear,
        glCodeDepartments,
        setGlCodesBudgetByYear,
        selectedGLBudget,
        setSelectedGLBudget,
        hasEditGLCodePermission,
        originalGlCodePeriodBudget,
        originalGlCodeYearBudget,
        departmentList,
        selectedPeriodDepartment,
        selectedYearDepartment,
        setSelectedPeriodDepartment,
        setSelectedYearDepartment,
        selectedCompany,
        selectedView,
        setSelectedView,
        isPeriodView,
        hasEditCheckbookPermission,
        hasRequireGlCodeFormatsPermission,
        hasManageBudgetPermission,
        hasCanOverrideBudgetsPermission,
        refreshGLCodePeriodTable,
        getGlCodePeriodBudget,
        hasAdjustBudgetPermission,
        hasUsesInitialBudgetsPermission,
        month,
        year,
        confirmBeforeProceeding,
        isPeriodTableDirty,
        isYearTableDirty,
        setOriginalGlCodeYearBudget,
        setOriginalGlCodePeriodBudget,
        pageTitle,
        setEditingGlCodeYearBudget,
        editingGlCodeYearBudget,
        setEditingGlCodePeriodBudget,
        dropDownIndexByYear,
        glPeriods,
        getGLPeriods,
        buId: glCodeDepartments.buId,
        handleSaveClick,
        committedAmountData,
        setCommittedAmountData,
        editingGlCodePeriodBudget,
        glCodeWarningsColumnData,
        setGlCodeWarningsColumnData,
        modalStepCount,
        setModalStepCount,
        isModalOpen,
        handleCloseModal,
        glFormatsResponse,
        regexPatternBE,
        periodErrors,
        openUsersAssigned,
        setOpenUsersAssigned,
        clients,
        assignedUsers,
        setAssignedUsers,
        hasManageGLCodeAttributesPermission,
        openWarningModal,
        isCheckbookEditable,
        isSelectedPeriodClosed,
        isSelectedFYPeriodsClosed,
        getIsLocked,
        isYearEditable,
        isSaveGlCodeBudgetLoading,
        allDeptGLCodes,
        uniquePeriodErrorList,
      }}
    >
      {children}
    </ViewCheckbookContext.Provider>
  );
};

export default ViewCheckbookContextProvider;
